import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfCtaText, sfHeadline, sfSub } from './sectionHelpers';

interface Article {
  task_id: string;
  name: string;
  description?: string;
  status: string;
  synthesized_result?: string;
}

const getApiBase = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.startsWith('/')) {
    return `${window.location.origin}${envUrl}`;
  }
  return envUrl || `${window.location.protocol}//${window.location.hostname}:8001/api`;
};

const BlogFeed: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const { siteId } = useParams<{ siteId: string }>();

  const maxArticles = parseInt(String(c.max_articles || '3'), 10) || 3;
  const showDate = c.show_date !== false;
  const showExcerpt = c.show_excerpt !== false;
  const ctaText = sfCtaText(c, 'Read more');

  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!siteId || fetched) return;

    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    const base = getApiBase();

    // First fetch the site to get project_id, then fetch tasks
    setLoading(true);
    fetch(`${base}/site-builder/sites/${siteId}`, { headers })
      .then(res => res.ok ? res.json() : null)
      .then(site => {
        if (!site?.project_id) return null;
        return fetch(`${base}/projects/${site.project_id}/tasks/?limit=100`, { headers })
          .then(res => res.ok ? res.json() : []);
      })
      .then((tasks: Article[] | null) => {
        if (tasks && Array.isArray(tasks)) {
          const completed = tasks.filter(t => t.status === 'completed');
          setArticles(completed.slice(0, maxArticles));
        }
        setFetched(true);
      })
      .catch(() => setFetched(true))
      .finally(() => setLoading(false));
  }, [siteId, maxArticles, fetched]);

  const getExcerpt = (article: Article): string => {
    const raw = article.description || '';
    if (raw.length <= 150) return raw;
    return raw.slice(0, 150).trimEnd() + '...';
  };

  const formatDate = (article: Article): string => {
    // Tasks don't have a date field on the client model, use a generic label
    return 'Recent';
  };

  const placeholderCards = Array.from({ length: maxArticles }).map((_, i) => (
    <div
      key={i}
      style={{
        background: '#f8fafc',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 12,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {showDate && (
        <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: 8 }}>
          —
        </p>
      )}
      <h3 style={{ fontSize: '1.1rem', color: '#1e293b' }}>Article Title {i + 1}</h3>
      {showExcerpt && (
        <p style={{ fontSize: '0.9rem', color: '#64748b', marginTop: 8 }}>
          Article excerpt will appear here once your content is published...
        </p>
      )}
      <span
        style={{
          display: 'inline-block',
          marginTop: 12,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'var(--brand-primary, #2177FF)',
        }}
      >
        {ctaText} &rarr;
      </span>
    </div>
  ));

  const articleCards = articles.map(article => (
    <div
      key={article.task_id}
      style={{
        background: '#f8fafc',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 12,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {showDate && (
        <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: 8 }}>
          {formatDate(article)}
        </p>
      )}
      <h3 style={{ fontSize: '1.1rem', color: '#1e293b' }}>{article.name}</h3>
      {showExcerpt && article.description && (
        <p style={{ fontSize: '0.9rem', color: '#64748b', marginTop: 8 }}>
          {getExcerpt(article)}
        </p>
      )}
      <a
        href={`/preview/${article.task_id}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-block',
          marginTop: 12,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'var(--brand-primary, #2177FF)',
          textDecoration: 'none',
        }}
      >
        {ctaText} &rarr;
      </a>
    </div>
  ));

  const showPlaceholders = !fetched || (fetched && articles.length === 0);

  return (
    <section style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ marginBottom: 8 }}
          >
            {sfHeadline(c, 'Latest Articles')}
          </InlineEditText>
          <InlineEditText
            tag="p"
            controlKey="subheadline"
            style={{ color: '#64748b', marginTop: 8, marginBottom: 40 }}
          >
            {sfSub(c, 'Stay up to date with our latest insights')}
          </InlineEditText>
        </div>

        {loading && (
          <p style={{ textAlign: 'center', color: '#94a3b8', marginBottom: 24 }}>
            Loading articles...
          </p>
        )}

        <div
          style={{
            display: 'grid',
            gap: 24,
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          }}
        >
          {showPlaceholders ? placeholderCards : articleCards}
        </div>

        {fetched && articles.length === 0 && !loading && (
          <p
            style={{
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: '0.875rem',
              marginTop: 16,
            }}
          >
            No published articles yet. Add content in your Content module to populate this feed.
          </p>
        )}
      </div>
    </section>
  );
};

export default BlogFeed;
